
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { TaskStatus, Role } from '../types';

const Dashboard: React.FC = () => {
  const { currentUser: user } = useAuth();
  const { projects, tasks, timeline } = useProject();
  const navigate = useNavigate();

  // Filter projects based on user visibility
  const visibleProjects = useMemo(() => {
    return projects.filter(p => {
      const canSeeAll = user?.role === Role.ADMIN || user?.role === Role.MANAGER;
      const isMember = p.memberIds.includes(user?.id || '');
      return canSeeAll || isMember;
    }) || [];
  }, [projects, user]);

  // Only consider non-archived projects for stats
  const activeProjects = useMemo(() => visibleProjects.filter(p => p.status !== 'archived'), [visibleProjects]);

  // Only consider tasks from visible active projects for overall stats
  const activeTasks = useMemo(() => {
    const activeProjectIds = new Set(activeProjects.map(p => p.id));
    return tasks.filter(t => activeProjectIds.has(t.projectId));
  }, [tasks, activeProjects]);

  // Real Productivity Calculation - Rounded to integer
  const productivityPercent = useMemo(() => {
    if (activeTasks.length === 0) return 0;
    const completed = activeTasks.filter(t => t.status === TaskStatus.DONE).length;
    return Math.round((completed / activeTasks.length) * 100);
  }, [activeTasks]);

  // Daily Streak and Activity Sparkline Calculation
  const streakData = useMemo(() => {
    if (!timeline || !user) return { streak: 0, activityDays: [] as boolean[] };
    
    const userEvents = timeline.filter(e => e.userId === user.id);
    const activeDates = new Set(userEvents.map(e => new Date(e.timestamp).toDateString()));
    
    // Calculate Streak
    let streak = 0;
    const checkDate = new Date();
    checkDate.setHours(0, 0, 0, 0);

    // If no activity today, check if the streak was alive until yesterday
    if (!activeDates.has(checkDate.toDateString())) {
      checkDate.setDate(checkDate.getDate() - 1);
    }

    while (activeDates.has(checkDate.toDateString())) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }

    // Calculate Activity for last 7 days (Mon-Sun)
    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sun) to 6 (Sat)
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    
    const weekActivity = [1, 2, 3, 4, 5, 6, 7].map(dayIndex => {
      const d = new Date(now);
      d.setDate(now.getDate() + mondayOffset + (dayIndex - 1));
      return activeDates.has(d.toDateString());
    });

    return { streak, activityDays: weekActivity };
  }, [timeline, user]);

  const stats = useMemo(() => {
    const counts = {
      [TaskStatus.TODO]: 0,
      [TaskStatus.IN_PROGRESS]: 0,
      [TaskStatus.DONE]: 0
    };
    activeTasks.forEach(t => counts[t.status]++);

    return [
      { name: 'Upcoming', value: counts[TaskStatus.TODO], color: '#94a3b8' },
      { name: 'Active', value: counts[TaskStatus.IN_PROGRESS], color: '#6366f1' },
      { name: 'Completed', value: counts[TaskStatus.DONE], color: '#10b981' }
    ];
  }, [activeTasks]);

  // Progress rounded to nearest integer
  const projectStatusData = activeProjects.map(p => {
    const pTasks = tasks.filter(t => t.projectId === p.id);
    const completed = pTasks.filter(t => t.status === TaskStatus.DONE).length;
    const progress = pTasks.length > 0 ? Math.round((completed / pTasks.length) * 100) : 0;
    return { name: p.name, progress };
  });

  const myTasks = activeTasks.filter(t => t.assigneeId === user?.id).slice(0, 5);

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-12 overflow-y-auto h-full p-4 md:p-8">
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Hi, {user?.name.split(' ')[0]} <span className="wave inline-block origin-[70%_70%] animate-[wave_2.5s_infinite]">👋</span>
          </h1>
          <p className="text-slate-500 font-medium mt-1">Here is what's happening across your projects today.</p>
        </div>
        <div className="flex gap-3">
           <button 
            className="flex-1 lg:flex-none px-6 py-3.5 bg-white border border-slate-200 text-slate-700 font-bold rounded-2xl hover:bg-slate-50 transition-all shadow-sm active:scale-95"
            onClick={() => navigate('/projects')}
          >
            All Projects
          </button>
          {user?.role !== Role.TEAM_MEMBER && (
            <button 
              onClick={() => navigate('/projects')}
              className="flex-1 lg:flex-none px-6 py-3.5 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/30 active:scale-95"
            >
              Start Project
            </button>
          )}
        </div>
      </header>

      {/* Quick Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Active Projects</p>
          <p className="text-3xl font-black text-slate-900 mt-1">{activeProjects.filter(p => p.status === 'active').length}</p>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Tasks Done</p>
          <p className="text-3xl font-black text-slate-900 mt-1">{activeTasks.filter(t => t.status === TaskStatus.DONE).length}</p>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Pending Work</p>
          <p className="text-3xl font-black text-slate-900 mt-1">{activeTasks.filter(t => t.status !== TaskStatus.DONE).length}</p>
        </div>
        <div className="bg-indigo-600 p-6 rounded-[2rem] shadow-xl shadow-indigo-500/20 text-white relative overflow-hidden group">
          <div className="relative z-10">
            <div className="w-12 h-12 bg-white/20 text-white rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest">Productivity</p>
            <p className="text-3xl font-black mt-1">{productivityPercent}%</p>
          </div>
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 lg:col-span-2">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
              Project Progression
            </h3>
            <select className="bg-slate-50 border-none rounded-xl text-xs font-bold px-3 py-2 outline-none">
              <option>Last 30 Days</option>
              <option>Last 7 Days</option>
            </select>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={projectStatusData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="#cbd5e1" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#cbd5e1" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip 
                  cursor={{ fill: '#f8fafc' }} 
                  contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px 16px' }}
                  // Fix: Handled number | undefined and ensure the tooltip shows integer percentages during animations.
                  formatter={(value: any) => [`${Math.round(Number(value) || 0)}%`, 'Completion']}
                />
                <Bar dataKey="progress" fill="url(#barGradient)" radius={[10, 10, 10, 10]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="font-extrabold text-slate-800 text-lg mb-8">Workload Distribution</h3>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats} dataKey="value" innerRadius={60} outerRadius={85} paddingAngle={8}>
                  {stats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '11px', fontWeight: 'bold' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {stats.map(s => (
              <div key={s.name} className="text-center p-3 bg-slate-50 rounded-2xl">
                <p className="text-xl font-black text-slate-900 leading-none">{s.value}</p>
                <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest mt-2">{s.name.split(' ')[0]}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden flex flex-col">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
            <h3 className="font-extrabold text-slate-800 text-lg">My Priority Stack</h3>
            <button className="text-xs font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-widest" onClick={() => navigate('/projects')}>View Queue</button>
          </div>
          <div className="divide-y divide-slate-50">
            {myTasks.length === 0 ? (
              <div className="p-16 text-center text-slate-400">
                <p className="font-medium italic">Nothing assigned to you right now.</p>
              </div>
            ) : (
              myTasks.map(task => (
                <div key={task.id} className="p-6 hover:bg-slate-50 transition-all flex items-center justify-between group cursor-pointer" onClick={() => navigate(`/projects/${task.projectId}`)}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-3 h-3 rounded-full shrink-0 ${
                      task.priority === 'High' ? 'bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.4)]' : 
                      task.priority === 'Medium' ? 'bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.4)]' : 'bg-slate-300'
                    }`} />
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">{task.title}</p>
                      <p className="text-[11px] text-slate-400 mt-1 font-semibold flex items-center gap-1.5">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z"/></svg>
                        Due {new Date(task.deadline).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                     <span className={`hidden sm:inline-block px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                      task.status === TaskStatus.DONE ? 'bg-emerald-100 text-emerald-700' : 
                      task.status === TaskStatus.IN_PROGRESS ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {task.status.replace('_', ' ')}
                    </span>
                    <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-[#1e293b] rounded-[2.5rem] shadow-xl p-10 flex flex-col justify-center items-center text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-600/20 to-transparent pointer-events-none" />
          <div className="relative z-10">
            <div className="w-20 h-20 bg-indigo-500/20 text-indigo-400 rounded-3xl flex items-center justify-center mb-6 mx-auto backdrop-blur-sm border border-indigo-500/20 shadow-2xl">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <h3 className="text-2xl font-black text-white">Daily Streak: {streakData.streak} {streakData.streak === 1 ? 'Day' : 'Days'}</h3>
            <p className="text-slate-400 mt-4 max-w-xs mx-auto font-medium">
              {streakData.streak > 0 
                ? `You're on fire! You've maintained activity for ${streakData.streak} consecutive days.`
                : "Start your first project activity to begin your daily streak!"
              }
            </p>
            <div className="mt-8 flex justify-center items-center gap-3">
              {['M','T','W','T','F','S','S'].map((day, i) => (
                <div key={i} className={`w-10 h-12 md:w-12 md:h-14 rounded-2xl flex flex-col items-center justify-center font-bold transition-all duration-500 ${
                  streakData.activityDays[i] 
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/40 border border-indigo-400/30' 
                    : 'bg-slate-800 text-slate-500 border border-slate-700'
                }`}>
                  <span className="text-[10px] uppercase font-black opacity-60 mb-1">{day}</span>
                  <div className={`w-1.5 h-1.5 rounded-full ${streakData.activityDays[i] ? 'bg-white' : 'bg-slate-700'}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
